import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useDragControls } from 'motion/react';

interface FolhaProps {
  aberto: boolean;
  aoFechar: () => void;
  titulo?: string;
  children: ReactNode;
  rodape?: ReactNode;
  /** Camada da folha no empilhamento (padrão 70). */
  zIndex?: number;
  /** Barra de arrastar no topo (padrão: aparece só no celular). */
  alca?: boolean;
  /** Espaçamento interno do conteúdo (padrão true). */
  padding?: boolean;
  larguraMaxima?: string;
  alturaMaxima?: string;
}

// Mola da folha: sobe rápido, sem quicar demais.
const MOLA = { type: 'spring' as const, stiffness: 380, damping: 34 };

// Folha que sobe de baixo: fundo escurece em 150 ms, fecha arrastando para
// baixo (> 100 px) ou tocando fora. Só transform e opacity.
export default function Folha({
  aberto,
  aoFechar,
  titulo,
  children,
  rodape,
  zIndex = 70,
  alca = true,
  padding = true,
  larguraMaxima = 'max-w-lg',
  alturaMaxima = '72vh',
}: FolhaProps) {
  const controles = useDragControls();
  const painel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!aberto) return;
    const aoEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', aoEsc);
      document.body.style.overflow = '';
    };
  }, [aberto, aoFechar]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-0 flex items-end justify-center sm:items-center sm:p-4"
      style={{ zIndex }}
    >
      <AnimatePresence>
        {aberto && (
          <motion.div
            key="veu"
            className="pointer-events-auto absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={aoFechar}
            aria-hidden
          />
        )}
        {aberto && (
          <motion.div
            key="folha"
            ref={painel}
            role="dialog"
            aria-modal="true"
            aria-label={titulo}
            className={`pointer-events-auto relative z-10 w-full overflow-hidden rounded-t-[22px] bg-background-50 sm:rounded-[22px] ${larguraMaxima}`}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={MOLA}
            drag="y"
            dragListener={false}
            dragControls={controles}
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 700) aoFechar();
            }}
          >
            {(alca || titulo) && (
              <div
                onPointerDown={(e) => controles.start(e)}
                className="cursor-grab touch-none active:cursor-grabbing"
              >
                {alca && (
                  <div className="flex justify-center pb-1 pt-3 sm:hidden">
                    <span className="h-1 w-10 rounded-full bg-background-300" />
                  </div>
                )}
                {titulo && (
                  <div className="flex items-center justify-between gap-3 px-5 pb-2 pt-3">
                    <h2 className="truncate text-[17px] font-extrabold text-foreground-950">
                      {titulo}
                    </h2>
                    <button
                      type="button"
                      onClick={aoFechar}
                      className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
                      aria-label="Fechar"
                    >
                      <i className="ri-close-line text-xl" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div
              className={`overflow-y-auto ${padding ? 'px-5 pb-6' : ''}`}
              style={{
                maxHeight: alturaMaxima,
                paddingBottom: padding ? 'max(1.5rem, env(safe-area-inset-bottom))' : undefined,
              }}
            >
              {children}
            </div>

            {rodape && (
              <div
                className="border-t border-background-200 px-5 py-4"
                style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
              >
                {rodape}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}